import { useCallback, useEffect, useRef, useState } from 'react';
import { DataListItem } from '@brighte/form-components';
import {
  ApplicationInputType,
  BNPL_REQUIRE_INCOME_VERIFICATION_FINANCE_AMOUNT_THRESHOLD,
  CustomerType,
  EmploymentType as ValidationEmploymentType,
  Period,
  requiredFieldError,
} from '@brighte/validation';
import {
  EmploymentType,
  OpenBankingConsentsQuery,
  OpenBankingIncomeQuery,
  useOpenBankingConsentsQuery,
} from '../../graphql';
import { determineApplicationSteps } from '../../pages/ApplicationForm/hooks';
import { useApplicationData, useOpenBankingSteps, useStepperActions, useStepperIndex } from '../../provider';
import {
  APPLICATION_STEPS,
  EOpenBankingError,
  INCOME_VERIFICATION,
  LABELS,
  OPEN_BANKING_CONSENTS_POLLING_INTERVAL,
  OPEN_BANKING_CONSENTS_TIMEOUT_DURATION,
  capitalise,
  convertAmountToMonthlyPeriod,
  formatDollars,
  productIds,
} from '../../utils';
import { INCOME_VERIFICATION_STRATEGIES, getFieldLabels, useIncomeVerificationConditionsProps } from './helpers';

function useIncomeVerificationSummary(): Array<{ title: string; items: DataListItem[] | [] }> | [] {
  const {
    IncomeVerification: incomeVerification,
    quote,
    Finance: finance,
  } = useApplicationData(['IncomeVerification', 'quote', 'Finance']);

  if (!incomeVerification || !quote) return [];
  const { applicant } = incomeVerification;

  const usingOpenBanking = applicant?.usingOpenBanking || false;
  const primary = applicant?.income?.primary;
  const financialProductId = quote?.financialProduct?.id || '';
  const isCashApplication = finance?.payment?.isCashApplication ?? false;

  const { NET_INCOME, EMPLOYER_NAME, ABN_NUMBER, SUMMARY_HEADER, EMPLOYMENT } = LABELS;
  const {
    BANK_STATEMENT,
    DOCUMENTS,
    VERIFICATION,
    INCOME_VERIFICATION_SUMMARY,
    INCOME_VERIFICATION_VIA_CDR_SUMMARY,
    EMPLOYMENT_TYPE,
  } = INCOME_VERIFICATION;
  let incomeVerificationTitle = '';

  if (applicant?.employment) {
    const {
      employment: { type, employed, selfEmployed, pensioner },
    } = applicant;

    const employmentTypeDetails = {
      [EmploymentType.EMPLOYED]: employed?.employerBusinessName,
      [EmploymentType.SELF_EMPLOYED]: selfEmployed?.abn,
      [EmploymentType.PENSIONER]: pensioner?.pensionType,
    };

    incomeVerificationTitle = `${capitalise(type)} ${employmentTypeDetails[type] || ''}`;
  }

  const employmentType = applicant?.employment?.type ?? '';

  // summary details will be different due to some fields are not available for brighte-flex e.g docs, bank statement, etc..
  if (financialProductId === productIds.FLEX) {
    return [
      {
        title: SUMMARY_HEADER,
        items: [
          {
            label: EMPLOYMENT_TYPE,
            value: capitalise(employmentType),
          },
          ...(applicant?.employment?.employed
            ? [
                {
                  label: EMPLOYER_NAME,
                  value: applicant?.employment.employed.employerBusinessName,
                },
              ]
            : []),
          ...(applicant?.employment?.selfEmployed
            ? [
                {
                  label: ABN_NUMBER,
                  value: applicant?.employment.selfEmployed.abn,
                },
              ]
            : []),
          {
            label: getFieldLabels('primary').income,
            value: `${formatDollars(primary?.amount)} ${primary?.period?.toLowerCase()}`,
          },
        ],
      },
    ];
  }

  if (financialProductId === productIds.ELECTRIFY_2515 && isCashApplication) {
    return [
      {
        title: SUMMARY_HEADER,
        items: [
          {
            label: EMPLOYMENT,
            value: incomeVerificationTitle,
          },
          {
            label: NET_INCOME,
            value: `${formatDollars(primary?.amount)} ${primary?.period?.toLowerCase()}`,
          },
        ],
      },
    ];
  }

  if (usingOpenBanking) {
    return [
      {
        title: SUMMARY_HEADER,
        items: [
          {
            label: EMPLOYMENT_TYPE,
            value: capitalise(employmentType),
          },
          ...(applicant?.employment?.employed
            ? [
                {
                  label: EMPLOYER_NAME,
                  value: applicant?.employment.employed.employerBusinessName,
                },
              ]
            : []),
          {
            label: INCOME_VERIFICATION_SUMMARY,
            value: INCOME_VERIFICATION_VIA_CDR_SUMMARY,
          },
        ],
      },
    ];
  }

  // Only surface a verification row when the applicant actually provided proof — otherwise "Documents
  // provided" shows even when nothing was uploaded (e.g. verification wasn't required).
  const hasBankStatement = !!primary?.bankStatementToken;
  const hasDocuments = (primary?.documents?.length ?? 0) > 0;

  const identificationSummary = [
    {
      title: SUMMARY_HEADER,
      items: [
        {
          label: EMPLOYMENT,
          value: incomeVerificationTitle,
        },
        {
          label: NET_INCOME,
          value: `${formatDollars(primary?.amount)} ${primary?.period?.toLowerCase()}`,
        },
        ...(hasBankStatement || hasDocuments
          ? [
              {
                label: VERIFICATION,
                value: hasBankStatement ? BANK_STATEMENT : DOCUMENTS,
              },
            ]
          : []),
        /* ...(isAdditionalIncome
          ? [
              {
                label: ADDITIONAL_INCOME,
                value: `${income.additional.amount} ${income.additional.period}`,
              },
              {
                label: VERIFICATION,
                value: income.additional.bankStatementToken ? BANK_STATEMENT : DOCUMENTS,
              },
            ]
          : []), */
      ],
    },
  ];

  return identificationSummary;
}

const useIncomeVerificationConditions = ({
  financialProductId,
  applicantPrimaryIncomeAmount,
  applicantPrimaryIncomePeriod,
  occupationIncomeThreshold,
  isDocumentsRequired,
  financeAmount,
  employmentType,
  pensionerType,
  dateOfBirth,
  isCashApplication,
}: useIncomeVerificationConditionsProps) => {
  // Get strategy for the financial product
  const strategy = INCOME_VERIFICATION_STRATEGIES[financialProductId] || INCOME_VERIFICATION_STRATEGIES.DEFAULT;

  const params: useIncomeVerificationConditionsProps = {
    financialProductId,
    applicantPrimaryIncomeAmount,
    applicantPrimaryIncomePeriod,
    occupationIncomeThreshold,
    isDocumentsRequired,
    financeAmount,
    employmentType,
    pensionerType,
    dateOfBirth,
    isCashApplication,
  };

  const isIncomeVerificationRequired = () => strategy.isIncomeVerificationRequired(params);

  const isAdditionalIncomeVerificationRequired = () =>
    strategy.isAdditionalVerificationRequired({
      ...params,
      isIncomeVerificationRequired: isIncomeVerificationRequired(), // needed as additional requirement in most cases, follow primary income verification condition except on certain special conditions
    });

  return { isIncomeVerificationRequired, isAdditionalIncomeVerificationRequired };
};

const useRedirectToStep = () => {
  const { previousStepIndex } = useStepperIndex();
  const { quote, Finance } = useApplicationData(['quote', 'Finance']);
  const { openBankingIncomeSyncState } = useOpenBankingSteps();

  const getSteps = (isUsingOpenBanking: boolean) => {
    const customerType = quote?.financialProduct.customerType ?? CustomerType.Residential;
    const isCashApplication = Finance?.payment?.isCashApplication;
    return determineApplicationSteps(customerType, isCashApplication, isUsingOpenBanking);
  };

  // If user is on summary step and income sync is in progress, redirect to income review step
  const shouldRedirectToIncomeReview = (isUsingOpenBanking: boolean) => {
    const steps = getSteps(Boolean(isUsingOpenBanking));
    const isSummaryStep = steps[previousStepIndex] === APPLICATION_STEPS.SUMMARY;
    const incomeReviewStepIndex = steps.findIndex(step => step === APPLICATION_STEPS.INCOME_REVIEW);
    const isValid = Boolean(
      isUsingOpenBanking &&
      openBankingIncomeSyncState.status === 'loading' &&
      incomeReviewStepIndex !== -1 &&
      isSummaryStep,
    );
    return { isValid, incomeReviewStepIndex };
  };

  // OpenBanking has 1 step than Manual. If user is on summary step and switches to manual, they need to be redirected to summary again
  const shouldRedirectToSummary = (isUsingOpenBanking: boolean) => {
    const steps = getSteps(Boolean(isUsingOpenBanking));
    const summaryStepIndex = steps.findIndex(step => step === APPLICATION_STEPS.SUMMARY);
    const isValid = previousStepIndex >= summaryStepIndex && summaryStepIndex !== -1;
    return { isValid, summaryStepIndex };
  };

  return { shouldRedirectToIncomeReview, shouldRedirectToSummary };
};

const useIncomeVerificationBeforeNextStep = () => {
  const { onNext } = useStepperActions();
  const { shouldRedirectToIncomeReview, shouldRedirectToSummary } = useRedirectToStep();
  const { setOpenBankingConsentComplete, setOpenBankingIncomeSyncState } = useOpenBankingSteps();

  const handleBeforeNextStep = (formValues: ApplicationInputType, isManualOccupationJobTitleMatched: boolean) => {
    if ('applicant' in formValues) {
      setOpenBankingConsentComplete(!!formValues.applicant?.usingOpenBanking);
    }
    if ('applicant' in formValues && !formValues.applicant?.usingOpenBanking) {
      setOpenBankingIncomeSyncState({ status: 'idle' });
    }

    const isUsingOpenBanking = 'applicant' in formValues && formValues.applicant?.usingOpenBanking;
    const incomeReviewRedirect = shouldRedirectToIncomeReview(isUsingOpenBanking);
    const summaryRedirect = shouldRedirectToSummary(isUsingOpenBanking);
    if (incomeReviewRedirect.isValid) {
      onNext(incomeReviewRedirect.incomeReviewStepIndex);
      return true;
    }

    if (summaryRedirect.isValid) {
      onNext(summaryRedirect.summaryStepIndex);
      return true;
    }

    if (isManualOccupationJobTitleMatched) {
      return false;
    }
    return false;
  };

  return { handleBeforeNextStep };
};

type RunGateCheck = (formValues: ApplicationInputType) => boolean;

type GateErrorFieldName =
  | 'applicant.employment.employed.code'
  | 'applicant.employment.selfEmployed.code'
  | 'applicant.income';

const useBrightePayOccupationGate = ({
  occupationIncomeThreshold,
  financeAmount,
  isOccupationLoading,
  isVerificationRequired,
  setGateResolved,
  setError,
}: {
  occupationIncomeThreshold: number;
  financeAmount: number;
  isOccupationLoading: boolean;
  isVerificationRequired: (formValues: ApplicationInputType) => boolean;
  setGateResolved: (value: boolean) => void;
  setError: (name: GateErrorFieldName, error: { type: string; message?: string } | null) => void;
}) => {
  const financeAmountRequiresVerification = financeAmount > BNPL_REQUIRE_INCOME_VERIFICATION_FINANCE_AMOUNT_THRESHOLD;

  const runGateCheck: RunGateCheck = useCallback(
    formValues => {
      if (!('applicant' in formValues) || !formValues.applicant) {
        return false;
      }

      const employment = formValues.applicant.employment;
      const employmentType = employment?.type;

      if (
        employmentType !== ValidationEmploymentType.EMPLOYED &&
        employmentType !== ValidationEmploymentType.SELF_EMPLOYED
      ) {
        return false;
      }

      const occupationCodePath =
        employmentType === ValidationEmploymentType.EMPLOYED
          ? 'applicant.employment.employed.code'
          : 'applicant.employment.selfEmployed.code';
      const occupationCode =
        employmentType === ValidationEmploymentType.EMPLOYED
          ? employment?.employed?.code
          : employment?.selfEmployed?.code;

      if (!occupationCode) {
        setError(occupationCodePath, { type: 'required', message: requiredFieldError() });
        return true;
      }

      if (financeAmountRequiresVerification) {
        setGateResolved(true);
        return true;
      }

      // `occupationIncomeThreshold` seeds to 0 and only hydrates once the occupation query
      // resolves, so while loading we cannot distinguish "no salary data for this occupation"
      // from "threshold not loaded yet". Block the transition without resolving the gate so the
      // sentinel/comparison branches below run against a hydrated value on the next attempt.
      if (isOccupationLoading) {
        return true;
      }

      const amount = formValues.applicant.income?.primary?.amount ?? 0;
      const period = formValues.applicant.income?.primary?.period ?? Period.Monthly;
      const monthlyIncome = convertAmountToMonthlyPeriod(amount, period);

      if (occupationIncomeThreshold === 0) {
        setGateResolved(true);
        return true;
      }

      if (monthlyIncome > occupationIncomeThreshold) {
        setGateResolved(true);
        return true;
      }

      // Income is within the occupation threshold, so primary income needs no verification. But
      // additional income proof is always required for Brighte Pay once the applicant opts in
      // (independent of the primary-income / finance-amount condition), so block here until it is
      // provided instead of letting the payload through. Admin skip (isSkipAdditionalIncome) is honored.
      const income = formValues.applicant.income;
      const additional = income?.additional;
      const additionalNeedsDocs =
        Boolean(income?.isAdditionalIncome) &&
        !income?.isSkipAdditionalIncome &&
        !(additional?.bankStatementToken || (additional?.documents?.length ?? 0) > 0);
      if (additionalNeedsDocs) {
        setError('applicant.income', { type: 'required', message: 'Income verification documents are required.' });
        return true;
      }

      setError('applicant.income', null);
      return false;
    },
    [financeAmountRequiresVerification, isOccupationLoading, occupationIncomeThreshold, setGateResolved, setError],
  );

  // brighte-pay's form schema explicitly skips the docs-required check, so on the manual
  // path (usingOpenBanking=false) this gate is the only thing blocking submission without
  // docs. `isVerificationRequired` is supplied by the parent so the rule stays in one place
  // alongside the UI's liveGateRequiresVerification — keeps pensioner handling consistent.
  const runDocumentCheck: RunGateCheck = useCallback(
    formValues => {
      if (!('applicant' in formValues) || !formValues.applicant) {
        return false;
      }
      if (formValues.applicant.usingOpenBanking !== false) {
        setError('applicant.income', null);
        return false;
      }

      if (!isVerificationRequired(formValues)) {
        setError('applicant.income', null);
        return false;
      }

      // Admin "Skip verification for now" sets isSkipPrimaryIncome / isSkipAdditionalIncome. The
      // form schema honors these flags (see packages/validation/src/form/incomeVerification.ts),
      // so this gate must mirror that to avoid blocking the admin escape hatch.
      const income = formValues.applicant.income;

      const primary = income?.primary;
      const primaryNeedsDocs =
        !income?.isSkipPrimaryIncome && !(primary?.bankStatementToken || (primary?.documents?.length ?? 0) > 0);

      const additional = income?.additional;
      const additionalNeedsDocs =
        Boolean(income?.isAdditionalIncome) &&
        !income?.isSkipAdditionalIncome &&
        !(additional?.bankStatementToken || (additional?.documents?.length ?? 0) > 0);

      if (primaryNeedsDocs || additionalNeedsDocs) {
        setError('applicant.income', { type: 'required', message: 'Income verification documents are required.' });
        return true;
      }

      setError('applicant.income', null);
      return false;
    },
    [isVerificationRequired, setError],
  );

  return { runGateCheck, runDocumentCheck };
};

function useReturnToIncomeDetail() {
  const { onNext } = useStepperActions();
  const { setOpenBankingConsentComplete } = useOpenBankingSteps();
  const { quote, Finance } = useApplicationData(['quote', 'Finance']);
  const isUsingOpenBanking = true;

  const getSteps = (isUsingOpenBanking: boolean) => {
    const customerType = quote?.financialProduct.customerType ?? CustomerType.Residential;
    const isCashApplication = Finance?.payment?.isCashApplication;
    return determineApplicationSteps(customerType, isCashApplication, isUsingOpenBanking);
  };

  const steps = getSteps(isUsingOpenBanking);
  const incomeReviewStepIndex = steps.findIndex(step => step === APPLICATION_STEPS.INCOME_REVIEW);

  const handleReturnToIncomeDetail = () => {
    if (incomeReviewStepIndex !== -1) {
      setOpenBankingConsentComplete(isUsingOpenBanking);
      onNext(incomeReviewStepIndex);
    }
  };

  return handleReturnToIncomeDetail;
}

function useOpenBankingConnectedBanks({
  enabled,
  onSuccess,
  setDisabledNavigation,
}: {
  enabled: boolean;
  onSuccess?: (data?: OpenBankingConsentsQuery) => void;
  setDisabledNavigation: (disabled: boolean) => void;
}) {
  const [state, setState] = useState<{ startTime: number | null; enablePolling: boolean; isLoading: boolean }>({
    startTime: null,
    enablePolling: false,
    isLoading: false,
  });
  const { setOpenBankingIncomeSyncState } = useOpenBankingSteps();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimeoutHandler = () => {
    if (timeoutRef.current) {
      clearInterval(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const resetState = () => {
    setState(prev => ({ ...prev, startTime: null, enablePolling: false, isLoading: false }));
    clearTimeoutHandler();
  };

  const { data, isFetching, refetch } = useOpenBankingConsentsQuery(undefined, {
    enabled,
    onSuccess: data => {
      if (data?.openBankingConsents?.length) {
        onSuccess?.(data);
        resetState();
      }
    },
    onError: () => {
      setOpenBankingIncomeSyncState({
        status: 'error',
        errorType: EOpenBankingError.CONSENT_UPSTREAM_PROCESSING_ERROR,
      });
      resetState();
    },
    refetchInterval: state.enablePolling ? OPEN_BANKING_CONSENTS_POLLING_INTERVAL : false,
  });

  const uniqueConnectedBanks =
    data?.openBankingConsents?.reduce<{
      seen: Record<string, boolean>;
      result: OpenBankingIncomeQuery['openBankingConsents'];
    }>(
      (acc, item) => {
        const institutionId = item.institutionId;

        if (institutionId && !acc.seen[institutionId]) {
          acc.seen[institutionId] = true;
          acc.result.push(item);
        }
        return acc;
      },
      { seen: {}, result: [] },
    ).result || [];

  const getOpenBankingConnectedBanks = () => {
    setState(prev => ({ ...prev, isLoading: true, startTime: Date.now(), enablePolling: true }));
    // Ensure we successfully receive the consent.received event from Fiskil.
    setTimeout(refetch, OPEN_BANKING_CONSENTS_POLLING_INTERVAL);
  };

  const handleTimeout = () => {
    if (!state.startTime || Date.now() - state.startTime < OPEN_BANKING_CONSENTS_TIMEOUT_DURATION) return;

    resetState();
  };

  useEffect(() => {
    if (state.startTime && state.enablePolling) {
      timeoutRef.current = setInterval(handleTimeout, OPEN_BANKING_CONSENTS_POLLING_INTERVAL);
    }

    const disabledNavigation = Boolean(
      state.isLoading && state.startTime && Date.now() - state.startTime < OPEN_BANKING_CONSENTS_TIMEOUT_DURATION,
    );
    setDisabledNavigation(disabledNavigation);

    return () => {
      clearTimeoutHandler();
    };
  }, [state]);

  return {
    isFetchingConnectedBanks: state.isLoading || isFetching,
    uniqueConnectedBanks,
    getOpenBankingConnectedBanks,
  };
}

export {
  useBrightePayOccupationGate,
  useIncomeVerificationConditions,
  useIncomeVerificationSummary,
  useReturnToIncomeDetail,
  useRedirectToStep,
  useIncomeVerificationBeforeNextStep,
  useOpenBankingConnectedBanks,
  type GateErrorFieldName,
};
